import { CommonModule } from '@angular/common';
import { Component, HostListener, input, output, signal } from '@angular/core';

export type SaleChannel = 'STORE' | 'WEB';

/**
 * Sell action requires the sale channel choice: confirm stays disabled (and
 * never emits) until STORE or WEB is chosen, so the sale is never recorded
 * without knowing whether it happened in-store or on the web.
 */
@Component({
  selector: 'app-sell-unit-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (visible()) {
      <dialog
        class="sell-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sell-unit-modal-title"
        [open]="true"
      >
        <div class="sell-modal__header">
          <h2 id="sell-unit-modal-title">Vender unidad</h2>
          <p>¿Dónde se realiza la venta de {{ unitLabel() }}?</p>
        </div>

        <div class="sell-modal__channels" role="radiogroup" aria-label="Canal de venta">
          <label class="sell-modal__channel">
            <input
              type="radio"
              name="sale-channel"
              value="STORE"
              [checked]="saleChannelDraft() === 'STORE'"
              [disabled]="isSubmitting()"
              (change)="choose('STORE')"
            />
            <span>Local</span>
          </label>
          <label class="sell-modal__channel">
            <input
              type="radio"
              name="sale-channel"
              value="WEB"
              [checked]="saleChannelDraft() === 'WEB'"
              [disabled]="isSubmitting()"
              (change)="choose('WEB')"
            />
            <span>Web</span>
          </label>
        </div>

        @if (saleChannelError(); as error) {
          <small class="sell-modal__error" role="alert">{{ error }}</small>
        }

        <div class="sell-modal__actions">
          <button
            type="button"
            class="app-action app-action--secondary"
            [disabled]="isSubmitting()"
            (click)="onCancel()"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="app-action app-action--primary"
            [disabled]="isSubmitting() || !canConfirm()"
            (click)="onConfirm()"
          >
            Confirmar venta
          </button>
        </div>
      </dialog>
    }
  `,
  styles: [
    `
      .sell-modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1000;
        min-width: 360px;
        max-width: 480px;
        padding: 24px;
        border: 1px solid #d3c4bd;
        border-radius: 12px;
        background: #faf6f3;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      }

      .sell-modal::backdrop {
        background: rgba(0, 0, 0, 0.4);
      }

      .sell-modal__header {
        margin-bottom: 20px;
      }

      .sell-modal__header h2 {
        margin: 0 0 8px;
        font-size: 1.1rem;
        color: #332b28;
      }

      .sell-modal__header p {
        margin: 0;
        font-size: 0.875rem;
        color: #7a6b66;
      }

      .sell-modal__channels {
        display: flex;
        gap: 12px;
        margin-bottom: 12px;
      }

      .sell-modal__channel {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        border: 1px solid #d3c4bd;
        border-radius: 10px;
        background: #ffffff;
        font-size: 0.875rem;
        color: #332b28;
        cursor: pointer;
      }

      .sell-modal__error {
        display: block;
        margin-bottom: 12px;
        color: #b91c1c;
        font-size: 0.8rem;
      }

      .sell-modal__actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
      }

      .sell-modal__actions .app-action {
        min-width: 100px;
      }
    `
  ]
})
export class SellUnitModalComponent {
  readonly visible = input<boolean>(false);
  readonly unitLabel = input<string>('');
  readonly isSubmitting = input<boolean>(false);

  readonly confirm = output<SaleChannel>();
  readonly cancel = output<void>();

  readonly saleChannelDraft = signal<SaleChannel | null>(null);
  readonly saleChannelError = signal<string | null>(null);

  canConfirm(): boolean {
    return this.saleChannelDraft() !== null;
  }

  choose(channel: SaleChannel): void {
    this.saleChannelDraft.set(channel);
    this.saleChannelError.set(null);
  }

  onConfirm(): void {
    const channel = this.saleChannelDraft();

    if (channel === null) {
      this.saleChannelError.set('Elegí si la venta se realiza en el local o por la web.');
      return;
    }

    this.confirm.emit(channel);
  }

  onCancel(): void {
    this.saleChannelDraft.set(null);
    this.saleChannelError.set(null);
    this.cancel.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible()) {
      this.onCancel();
    }
  }
}
